/**
 * =============================================================================
 * WlBillingTrendGrid
 *
 * Grid 2x2 de KPIs de tendência (12 meses) da aba Cobrança do painel WL.
 * Reutiliza `WlKpiSparkline` (modo line) para as 4 séries:
 *
 *   1. Taxa de inadimplência   — % vencido/emitido no mês
 *   2. Receitas perdidas       — canceladas + parcelas 120+ dias
 *   3. Ticket médio recebido   — média valor_pago das parcelas pagas
 *   4. Taxa de recebimento     — % recebido/emitido no mês
 *
 * Cada card mostra o valor do MÊS CORRENTE e um ícone (?) com a regra.
 *
 * @version 1.0.0
 * RELEASE: 2026-07-22 v1.0.0 - Versão inicial.
 * =============================================================================
 */
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  TrendingDown,
  DollarSign,
  Receipt,
  Percent,
  HelpCircle,
} from 'lucide-react';
import { WlKpiSparkline } from './WlKpiSparkline';
import type { WlBillingKpisPayload } from '@/hooks/wl/useWlBillingKpis';

interface Props {
  data?: WlBillingKpisPayload;
  isLoading?: boolean;
}

const fmtBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(v || 0));

const fmtPct = (v: number) => `${Number(v || 0).toFixed(1)}%`;

/** Card de tendência: título, valor atual, sparkline e tooltip de ajuda. */
function TrendCard({
  icon: Icon,
  label,
  value,
  help,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
  help: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon className="h-4 w-4" />
          <span>{label}</span>
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`Ajuda: ${label}`}
                  className="inline-flex"
                >
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60 hover:text-muted-foreground" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs">
                {help}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <div className="text-2xl font-semibold leading-none">{value}</div>
      {children}
    </Card>
  );
}

export function WlBillingTrendGrid({ data, isLoading }: Props) {
  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[200px] w-full" />
        ))}
      </div>
    );
  }

  const trend = data.trend;
  const last = <T extends { value: number }>(arr: T[]): number =>
    arr.length ? Number(arr[arr.length - 1].value || 0) : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
      <TrendCard
        icon={TrendingDown}
        label="Taxa de inadimplência"
        value={fmtPct(last(trend.inadimplencia_pct))}
        help="Percentual de valor vencido sobre o total emitido no mês. Fórmula: (soma de parcelas vencidas / soma de cobranças emitidas) × 100. Considera o mês de vencimento das parcelas."
      >
        <WlKpiSparkline
          mode="line"
          data={trend.inadimplencia_pct}
          color="#f5a524"
          formatValue={fmtPct}
        />
      </TrendCard>

      <TrendCard
        icon={DollarSign}
        label="Receitas perdidas"
        value={fmtBRL(last(trend.receitas_perdidas))}
        help="Soma das cobranças canceladas no mês MAIS o valor de parcelas em aberto vencidas há mais de 120 dias (consideradas praticamente irrecuperáveis)."
      >
        <WlKpiSparkline
          mode="line"
          data={trend.receitas_perdidas}
          color="#ef4444"
          formatValue={fmtBRL}
        />
      </TrendCard>

      <TrendCard
        icon={Receipt}
        label="Ticket médio recebido"
        value={fmtBRL(last(trend.ticket_medio))}
        help="Média do valor efetivamente pago por parcela no mês. Fórmula: soma dos valores pagos / número de parcelas pagas (com data de pagamento no mês)."
      >
        <WlKpiSparkline
          mode="line"
          data={trend.ticket_medio}
          color="#8b7cf6"
          formatValue={fmtBRL}
        />
      </TrendCard>

      <TrendCard
        icon={Percent}
        label="Taxa de recebimento"
        value={fmtPct(last(trend.taxa_recebimento))}
        help="Percentual do total emitido que foi efetivamente recebido no mês. Fórmula: (soma recebido no mês / soma emitido no mês) × 100. Indica a eficiência de cobrança."
      >
        <WlKpiSparkline
          mode="line"
          data={trend.taxa_recebimento}
          color="#22c55e"
          formatValue={fmtPct}
        />
      </TrendCard>
    </div>
  );
}
